import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Plus, Trash2, GripVertical, Eye, CheckCircle, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
const uuidv4 = () => Math.random().toString(36).substr(2, 9) + Date.now().toString(36);

const STEP_TYPES = [
  { value: "rename", label: "Rename Column" },
  { value: "type_convert", label: "Type Conversion" },
  { value: "date_format", label: "Date Format" },
  { value: "string_op", label: "String Operation" },
  { value: "string_concat", label: "Concat Columns" },
  { value: "numeric_op", label: "Numeric Operation" },
  { value: "conditional", label: "Conditional (If/Else)" },
  { value: "value_map", label: "Value Map" },
  { value: "calculated", label: "Calculated Field" },
  { value: "select", label: "Select / Reorder Columns" },
  { value: "lookup", label: "Lookup Table Join" },
];

const STRING_OPS = ["trim", "case", "pad", "extract", "replace", "split", "prefix", "suffix"];
const NUMERIC_OPS = ["round", "arithmetic", "abs", "floor", "ceil", "currency"];
const CASE_MODES = ["upper", "lower", "title", "sentence"];
const ARITH_OPS = ["multiply", "divide", "add", "subtract", "modulo"];

// ── Column state simulation ──────────────────────────────────────────────

/**
 * Returns a new Set of column names after applying one pipeline step.
 * Pure function — does not mutate the input set.
 */
function applyStepToColSet(step, colSet) {
  const next = new Set(colSet);
  if (!step.enabled) return next;
  const cfg = step.config || {};
  switch (step.type) {
    case "rename":
      if (cfg.old_name && cfg.new_name && next.has(cfg.old_name)) {
        next.delete(cfg.old_name);
        if (cfg.new_name) next.add(cfg.new_name);
      }
      break;
    case "select":
      if (cfg.columns && cfg.columns.length > 0) {
        const sel = new Set(cfg.columns);
        for (const c of [...next]) { if (!sel.has(c)) next.delete(c); }
      }
      break;
    case "string_concat":
      if (cfg.target_column) next.add(cfg.target_column);
      break;
    case "calculated":
      if (cfg.output_column) next.add(cfg.output_column);
      break;
    case "lookup":
      if (cfg.output_column) next.add(cfg.output_column);
      break;
    case "conditional":
      if (cfg.output_column) next.add(cfg.output_column);
      break;
    case "value_map":
      if (cfg.output_column) next.add(cfg.output_column);
      break;
    default:
      break; // type_convert, date_format, string_op, numeric_op — in-place, no column name changes
  }
  return next;
}

/** Returns new column names introduced by a step (not in the input set). */
function getStepNewColumns(step, colSet) {
  const after = applyStepToColSet(step, colSet);
  return [...after].filter(c => !colSet.has(c));
}

/** Returns columns required by a step to already exist. */
function getStepRequiredColumns(step) {
  const cfg = step.config || {};
  const req = [];
  switch (step.type) {
    case "rename":
      if (cfg.old_name) req.push(cfg.old_name);
      break;
    case "type_convert":
    case "date_format":
    case "string_op":
    case "numeric_op":
      if (cfg.column) req.push(cfg.column);
      break;
    case "string_concat":
      (cfg.source_columns || []).forEach(c => req.push(c));
      break;
    case "conditional":
      if (cfg.condition_column) req.push(cfg.condition_column);
      break;
    case "value_map":
      if (cfg.column) req.push(cfg.column);
      break;
    case "lookup":
      if (cfg.lookup_column) req.push(cfg.lookup_column);
      break;
    case "calculated": {
      const matches = [...(cfg.expression || "").matchAll(/\{([^}]+)\}/g)];
      matches.forEach(m => req.push(m[1].trim()));
      break;
    }
    default:
      break;
  }
  return req.filter(Boolean);
}

/** Returns columns available at stepIndex (after applying steps 0…stepIndex-1). */
function getColumnsAtStep(pipeline, originalCols, stepIndex) {
  let cols = new Set(originalCols);
  for (let i = 0; i < stepIndex; i++) {
    cols = applyStepToColSet(pipeline[i], cols);
  }
  return [...cols].sort((a, b) => a.localeCompare(b));
}

/**
 * Validates the pipeline order. Returns array of { stepIndex, message, missingColumns }.
 * A non-empty return means at least one step references a column not yet available.
 */
function validatePipelineOrder(pipeline, originalCols) {
  const errors = [];
  let cols = new Set(originalCols);
  for (let i = 0; i < pipeline.length; i++) {
    const step = pipeline[i];
    if (!step.enabled) { cols = applyStepToColSet(step, cols); continue; }
    const required = getStepRequiredColumns(step);
    const missing = required.filter(r => !cols.has(r));
    if (missing.length > 0) {
      const label = STEP_TYPES.find(t => t.value === step.type)?.label || step.type;
      errors.push({
        stepIndex: i,
        stepId: step.id,
        missingColumns: missing,
        message: `Step ${i + 1} "${label}" needs "${missing.join('", "')}" — not available at this position`,
      });
    }
    cols = applyStepToColSet(step, cols);
  }
  return errors;
}

function StepForm({ step, columns, lookupTables, onChange }) {
  const cfg = step.config || {};
  const set = (key, val) => onChange({ ...step, config: { ...cfg, [key]: val } });

  // For regex tester (string_op / extract)
  const [regexTestInput, setRegexTestInput] = useState("");
  const [regexTestResult, setRegexTestResult] = useState(null);

  // Sync lookup_type AND pre-populate column config from the selected lookup table
  useEffect(() => {
    if (step.type !== "lookup") return;
    const tableId = cfg.lookup_table_id;
    if (!tableId) return;
    const selectedTable = lookupTables.find(t => t._id === tableId);
    if (!selectedTable) return;
    const tableType = selectedTable.lookup_type || "key_value";
    const tableCols = selectedTable.columns || [];
    const updates = {};

    // Always sync the lookup_type
    if (cfg.lookup_type !== tableType) updates.lookup_type = tableType;

    if (tableType === "range") {
      // Pre-populate bound and value columns from the table's actual columns
      if (!cfg.lower_column && tableCols[0]) updates.lower_column = tableCols[0];
      if (!cfg.upper_column && tableCols[1]) updates.upper_column = tableCols[1];
      if (!cfg.lookup_value_column && tableCols[2]) updates.lookup_value_column = tableCols[2];
    } else {
      // Key-value: pre-populate key column and value column
      if (!cfg.lookup_key_column && tableCols[0]) updates.lookup_key_column = tableCols[0];
      if (!cfg.lookup_value_column && tableCols[1]) updates.lookup_value_column = tableCols[1];
    }

    if (Object.keys(updates).length > 0) {
      onChange({ ...step, config: { ...cfg, ...updates } });
    }
  }, [cfg.lookup_table_id, lookupTables]); // eslint-disable-line

  const testRegex = () => {
    try {
      const pattern = cfg.pattern || "";
      const flags = cfg.flags || "";
      const group = parseInt(cfg.group ?? 0);
      const re = new RegExp(pattern, flags);
      const m = re.exec(regexTestInput);
      if (m) {
        setRegexTestResult({ match: true, value: m[group] ?? m[0], groups: Array.from(m) });
      } else {
        setRegexTestResult({ match: false });
      }
    } catch (e) {
      setRegexTestResult({ error: e.message });
    }
  };

  if (step.type === "rename") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-500">Source Column</Label>
          <Select value={cfg.old_name || ""} onValueChange={v => set("old_name", v)}>
            <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>{columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">New Name</Label>
          <Input className="h-8 text-sm mt-1" value={cfg.new_name || ""} onChange={e => set("new_name", e.target.value)} placeholder="new_name" />
        </div>
      </div>
    );
  }

  if (step.type === "type_convert") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-500">Column</Label>
          <Select value={cfg.column || ""} onValueChange={v => set("column", v)}>
            <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>{columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Convert To</Label>
          <Select value={cfg.target_type || "text"} onValueChange={v => set("target_type", v)}>
            <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["text","number","boolean","date"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  if (step.type === "date_format") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-500">Column</Label>
          <Select value={cfg.column || ""} onValueChange={v => set("column", v)}>
            <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>{columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Source Format</Label>
          <Input className="h-8 text-sm mt-1" value={cfg.source_format || "auto"} onChange={e => set("source_format", e.target.value)} placeholder="auto / %d/%m/%Y" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Target Format</Label>
          <Input className="h-8 text-sm mt-1" value={cfg.target_format || "%Y-%m-%d"} onChange={e => set("target_format", e.target.value)} placeholder="%Y-%m-%d" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Target Timezone (optional)</Label>
          <Input className="h-8 text-sm mt-1" value={cfg.timezone_target || ""} onChange={e => set("timezone_target", e.target.value)} placeholder="UTC / America/New_York" />
        </div>
      </div>
    );
  }

  if (step.type === "string_op") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Column</Label>
            <Select value={cfg.column || ""} onValueChange={v => set("column", v)}>
              <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>{columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Operation</Label>
            <Select value={cfg.operation || "trim"} onValueChange={v => set("operation", v)}>
              <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{STRING_OPS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {cfg.operation === "case" && (
          <div>
            <Label className="text-xs text-gray-500">Case Mode</Label>
            <Select value={cfg.mode || "upper"} onValueChange={v => set("mode", v)}>
              <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{CASE_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {cfg.operation === "trim" && (
          <div>
            <Label className="text-xs text-gray-500">Trim Mode</Label>
            <Select value={cfg.mode || "both"} onValueChange={v => set("mode", v)}>
              <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{["both","leading","trailing"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {cfg.operation === "extract" && (
          <div className="space-y-3 bg-gray-50 rounded-md p-3 border border-gray-200">
            <div>
              <Label className="text-xs text-gray-500">Extraction Method</Label>
              <Select value={cfg.method || "substring"} onValueChange={v => set("method", v)}>
                <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="substring">Substring (start + length)</SelectItem>
                  <SelectItem value="regex">Regular Expression</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {cfg.method === "substring" || !cfg.method ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Start Index (0-based)</Label>
                  <Input className="h-8 text-sm mt-1" type="number" min={0} value={cfg.start ?? 0} onChange={e => set("start", parseInt(e.target.value))} />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Length (blank = to end)</Label>
                  <Input className="h-8 text-sm mt-1" type="number" min={1} value={cfg.length ?? ""} onChange={e => set("length", e.target.value ? parseInt(e.target.value) : undefined)} placeholder="to end" />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs text-gray-500">Regex Pattern</Label>
                    <Input
                      className="h-8 text-sm mt-1 font-mono"
                      value={cfg.pattern || ""}
                      onChange={e => set("pattern", e.target.value)}
                      placeholder="(\d{4}-\d{2}-\d{2})"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Flags</Label>
                    <Input className="h-8 text-sm mt-1 font-mono" value={cfg.flags || ""} onChange={e => set("flags", e.target.value)} placeholder="gi" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Capture Group # (0 = full match)</Label>
                  <Input className="h-8 text-sm mt-1 w-24" type="number" min={0} value={cfg.group ?? 0} onChange={e => set("group", parseInt(e.target.value))} />
                </div>
                {/* Regex Tester */}
                <div className="border border-[#006080]/20 rounded-md p-3 bg-white">
                  <Label className="text-xs font-semibold text-[#006080] uppercase tracking-wider">Regex Tester</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      className="h-7 text-xs flex-1 font-mono"
                      value={regexTestInput}
                      onChange={e => setRegexTestInput(e.target.value)}
                      placeholder="Paste sample text to test..."
                    />
                    <Button size="sm" className="h-7 px-3 text-xs bg-[#006080] text-white hover:bg-[#004d66]" onClick={testRegex}>
                      Test
                    </Button>
                  </div>
                  {regexTestResult && (
                    <div className={`mt-2 p-2 rounded text-xs font-mono ${regexTestResult.error ? "bg-red-50 text-red-700" : regexTestResult.match ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-500"}`}>
                      {regexTestResult.error && <span>Error: {regexTestResult.error}</span>}
                      {!regexTestResult.error && regexTestResult.match && (
                        <div>
                          <span className="font-semibold">Match found!</span>
                          <div className="mt-1">Group {cfg.group ?? 0}: <span className="bg-green-100 px-1 rounded">{regexTestResult.value}</span></div>
                          {regexTestResult.groups.length > 1 && (
                            <div className="mt-1 text-gray-500">All groups: {regexTestResult.groups.map((g, i) => `[${i}]: ${g}`).join(" | ")}</div>
                          )}
                        </div>
                      )}
                      {!regexTestResult.error && !regexTestResult.match && <span>No match found</span>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {cfg.operation === "replace" && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Find</Label>
                <Input className="h-8 text-sm mt-1 font-mono" value={cfg.find || ""} onChange={e => set("find", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Replace With</Label>
                <Input className="h-8 text-sm mt-1 font-mono" value={cfg.replace || ""} onChange={e => set("replace", e.target.value)} />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-3.5 h-3.5" checked={cfg.use_regex || false} onChange={e => set("use_regex", e.target.checked)} />
              <span className="text-xs text-gray-500">Use regex for find pattern</span>
            </label>
          </div>
        )}
        {(cfg.operation === "prefix" || cfg.operation === "suffix") && (
          <div>
            <Label className="text-xs text-gray-500">Value</Label>
            <Input className="h-8 text-sm mt-1" value={cfg.value || ""} onChange={e => set("value", e.target.value)} placeholder="Text to add..." />
          </div>
        )}
        {cfg.operation === "split" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Delimiter</Label>
              <Input className="h-8 text-sm mt-1 font-mono" value={cfg.delimiter || ","} onChange={e => set("delimiter", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Part (0-based)</Label>
              <Input className="h-8 text-sm mt-1" type="number" min={0} value={cfg.part ?? 0} onChange={e => set("part", parseInt(e.target.value))} />
            </div>
          </div>
        )}
        {cfg.operation === "pad" && (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Side</Label>
              <Select value={cfg.side || "right"} onValueChange={v => set("side", v)}>
                <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Total Length</Label>
              <Input className="h-8 text-sm mt-1" type="number" min={1} value={cfg.length ?? 10} onChange={e => set("length", parseInt(e.target.value))} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Pad Character</Label>
              <Input className="h-8 text-sm mt-1 font-mono" maxLength={1} value={cfg.char || " "} onChange={e => set("char", e.target.value)} />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (step.type === "string_concat") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Target Column Name</Label>
            <Input className="h-8 text-sm mt-1" value={cfg.target_column || ""} onChange={e => set("target_column", e.target.value)} placeholder="concatenated" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Separator</Label>
            <Input className="h-8 text-sm mt-1" value={cfg.separator ?? " "} onChange={e => set("separator", e.target.value)} placeholder="space, comma, etc." />
          </div>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Source Columns (select multiple)</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {columns.map(c => {
              const sel = (cfg.source_columns || []).includes(c);
              return (
                <button
                  key={c}
                  className={`text-xs px-2 py-1 rounded border transition-colors ${sel ? "bg-[#006080] text-white border-[#006080]" : "bg-white border-gray-200 text-gray-600 hover:border-[#006080]"}`}
                  onClick={() => {
                    const cur = cfg.source_columns || [];
                    set("source_columns", sel ? cur.filter(x => x !== c) : [...cur, c]);
                  }}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (step.type === "numeric_op") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-500">Column</Label>
          <Select value={cfg.column || ""} onValueChange={v => set("column", v)}>
            <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>{columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Operation</Label>
          <Select value={cfg.operation || "round"} onValueChange={v => set("operation", v)}>
            <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{NUMERIC_OPS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {cfg.operation === "round" && (
          <div>
            <Label className="text-xs text-gray-500">Decimal Places</Label>
            <Input className="h-8 text-sm mt-1" type="number" min={0} value={cfg.decimals ?? 2} onChange={e => set("decimals", parseInt(e.target.value))} />
          </div>
        )}
        {cfg.operation === "arithmetic" && (
          <>
            <div>
              <Label className="text-xs text-gray-500">Operator</Label>
              <Select value={cfg.operator || "multiply"} onValueChange={v => set("operator", v)}>
                <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{ARITH_OPS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Operand</Label>
              <Input className="h-8 text-sm mt-1" type="number" value={cfg.operand ?? 1} onChange={e => set("operand", parseFloat(e.target.value))} />
            </div>
          </>
        )}
      </div>
    );
  }

  if (step.type === "conditional") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-500">Check Column</Label>
          <Select value={cfg.condition_column || ""} onValueChange={v => set("condition_column", v)}>
            <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>{columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Operator</Label>
          <Select value={cfg.operator || "equals"} onValueChange={v => set("operator", v)}>
            <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["equals","not_equals","contains","starts_with","ends_with","greater_than","less_than","is_empty","is_not_empty"].map(o =>
                <SelectItem key={o} value={o}>{o.replace(/_/g," ")}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        {!["is_empty","is_not_empty"].includes(cfg.operator) && (
          <div>
            <Label className="text-xs text-gray-500">Compare Value</Label>
            <Input className="h-8 text-sm mt-1" value={cfg.compare_value || ""} onChange={e => set("compare_value", e.target.value)} />
          </div>
        )}
        <div>
          <Label className="text-xs text-gray-500">Output Column</Label>
          <Input className="h-8 text-sm mt-1" value={cfg.output_column || ""} onChange={e => set("output_column", e.target.value)} placeholder="same as condition col" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">If True →</Label>
          <Input className="h-8 text-sm mt-1" value={cfg.true_value || ""} onChange={e => set("true_value", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs text-gray-500">If False →</Label>
          <Input className="h-8 text-sm mt-1" value={cfg.false_value || ""} onChange={e => set("false_value", e.target.value)} />
        </div>
      </div>
    );
  }

  if (step.type === "value_map") {
    const mapping = cfg.mapping || [];
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Source Column</Label>
            <Select value={cfg.column || ""} onValueChange={v => set("column", v)}>
              <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>{columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Output Column</Label>
            <Input className="h-8 text-sm mt-1" value={cfg.output_column || ""} onChange={e => set("output_column", e.target.value)} placeholder="same as source" />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs text-gray-500">Value Mappings</Label>
            <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => set("mapping", [...mapping, { from: "", to: "" }])}>
              <Plus size={10} className="mr-1" /> Add
            </Button>
          </div>
          {mapping.map((m, idx) => (
            <div key={idx} className="flex gap-2 mb-1">
              <Input className="h-7 text-xs flex-1" value={m.from} onChange={e => { const nm = [...mapping]; nm[idx]={ ...m, from: e.target.value }; set("mapping", nm); }} placeholder="From value" />
              <span className="text-gray-400 self-center">→</span>
              <Input className="h-7 text-xs flex-1" value={m.to} onChange={e => { const nm = [...mapping]; nm[idx]={ ...m, to: e.target.value }; set("mapping", nm); }} placeholder="To value" />
              <button onClick={() => set("mapping", mapping.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
        <div>
          <Label className="text-xs text-gray-500">Default (if not found)</Label>
          <Input className="h-8 text-sm mt-1" value={cfg.default_value || ""} onChange={e => set("default_value", e.target.value)} placeholder="Leave empty to keep original" />
        </div>
      </div>
    );
  }

  if (step.type === "calculated") {
    return (
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-gray-500">Output Column Name</Label>
          <Input className="h-8 text-sm mt-1" value={cfg.output_column || ""} onChange={e => set("output_column", e.target.value)} placeholder="calculated_field" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Expression</Label>
          <textarea
            className="w-full h-20 px-2 py-1.5 text-sm font-mono border border-gray-200 rounded-md focus:outline-none focus:border-[#006080] mt-1 resize-none"
            value={cfg.expression || ""}
            onChange={e => set("expression", e.target.value)}
            placeholder={'IF({age} > 18, "Yes", "No")'}
          />
          <div className="mt-1.5 p-2 bg-[#F8F9FA] rounded-md border border-gray-100 text-xs text-gray-500 space-y-0.5">
            <div><span className="font-mono text-[#006080]">{"IF({col} > 18, \"Yes\", \"No\")"}</span> — function syntax</div>
            <div><span className="font-mono text-[#006080]">{"IF {col} > 18 THEN \"Yes\" ELSE \"No\""}</span> — also supported</div>
            <div className="text-gray-400 pt-0.5">Use <span className="font-mono">{"{ColumnName}"}</span> for col refs. Numeric strings auto-convert for comparisons. Functions: IF, LEN, UPPER, LOWER, TRIM, ROUND, CONCAT, LEFT, RIGHT, MID, NUM</div>
          </div>
        </div>
      </div>
    );
  }

  if (step.type === "select") {
    return (
      <div>
        <Label className="text-xs text-gray-500 mb-1 block">Select & Reorder Columns</Label>
        <div className="flex flex-wrap gap-1.5">
          {columns.map(c => {
            const sel = (cfg.columns || columns).includes(c);
            return (
              <button
                key={c}
                className={`text-xs px-2 py-1 rounded border transition-colors ${sel ? "bg-[#006080] text-white border-[#006080]" : "bg-white border-gray-200 text-gray-400"}`}
                onClick={() => {
                  const cur = cfg.columns || [...columns];
                  set("columns", sel ? cur.filter(x => x !== c) : [...cur, c]);
                }}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (step.type === "lookup") {
    const selectedTable = lookupTables.find(t => t._id === cfg.lookup_table_id);
    const tableType = selectedTable?.lookup_type || "key_value";
    const tableColumns = selectedTable?.columns || [];

    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Source Column (to look up)</Label>
            <Select value={cfg.lookup_column || ""} onValueChange={v => set("lookup_column", v)}>
              <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>{columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Lookup Table</Label>
            <Select value={cfg.lookup_table_id || ""} onValueChange={v => { set("lookup_table_id", v); }}>
              <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>{lookupTables.map(t => <SelectItem key={t._id} value={t._id}>{t.name} ({t.lookup_type === "range" ? "Range" : "Key→Value"})</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {selectedTable && tableType === "key_value" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Key Column (in lookup table)</Label>
              <Select value={cfg.lookup_key_column || ""} onValueChange={v => set("lookup_key_column", v)}>
                <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{tableColumns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Value Column (to return)</Label>
              <Select value={cfg.lookup_value_column || ""} onValueChange={v => set("lookup_value_column", v)}>
                <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{tableColumns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
        {selectedTable && tableType === "range" && (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Lower Column</Label>
              <Select value={cfg.lower_column || tableColumns[0] || ""} onValueChange={v => set("lower_column", v)}>
                <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{tableColumns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Upper Column</Label>
              <Select value={cfg.upper_column || tableColumns[1] || ""} onValueChange={v => set("upper_column", v)}>
                <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{tableColumns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Return Column</Label>
              <Select value={cfg.lookup_value_column || tableColumns[2] || ""} onValueChange={v => set("lookup_value_column", v)}>
                <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{tableColumns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Output Column Name</Label>
            <Input className="h-8 text-sm mt-1" value={cfg.output_column || ""} onChange={e => set("output_column", e.target.value)} placeholder="lookup_result" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Default (if not found)</Label>
            <Input className="h-8 text-sm mt-1" value={cfg.default_value || ""} onChange={e => set("default_value", e.target.value)} placeholder="null" />
          </div>
        </div>
        {/* lookup_type is now synced via useEffect — no inline call needed */}
      </div>
    );
  }

  return <p className="text-xs text-gray-400">Select a step type to configure.</p>;
}

export default function TransformPage() {
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const [upload, setUpload] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [lookupTables, setLookupTables] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [expandedStep, setExpandedStep] = useState(null);
  const [error, setError] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [dragError, setDragError] = useState("");

  const columns = upload?.columns?.map(c => c.name) || [];

  useEffect(() => {
    client.get(`/files/${uploadId}`).then(r => {
      setUpload(r.data);
      setPipeline(r.data.pipeline || []);
    });
    client.get("/lookups/").then(r => setLookupTables(r.data));
  }, [uploadId]);

  const addStep = (type) => {
    const newStep = { id: uuidv4(), type, enabled: true, config: {} };
    setPipeline([...pipeline, newStep]);
    setExpandedStep(newStep.id);
  };

  const updateStep = (id, updated) => {
    setPipeline(pipeline.map(s => s.id === id ? updated : s));
  };

  const removeStep = (id) => {
    setPipeline(pipeline.filter(s => s.id !== id));
    if (expandedStep === id) setExpandedStep(null);
  };

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", idx);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(idx);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    const newPipeline = [...pipeline];
    const [removed] = newPipeline.splice(dragIdx, 1);
    newPipeline.splice(idx, 0, removed);

    // Validate dependency order before applying
    const errors = validatePipelineOrder(newPipeline, columns);
    if (errors.length > 0) {
      setDragError(errors.map(e => e.message).join(" · "));
      setDragIdx(null); setDragOverIdx(null);
      // Auto-clear error after 5s
      setTimeout(() => setDragError(""), 5000);
      return;
    }

    setPipeline(newPipeline);
    setDragIdx(null);
    setDragOverIdx(null);
    setDragError("");
  };

  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const savePipeline = async () => {
    setSaving(true);
    try {
      await client.put(`/files/${uploadId}/pipeline`, { pipeline });
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const res = await client.post("/transforms/preview", { upload_id: uploadId, pipeline, limit: 5 });
      setPreviewRows(res.data.rows);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const previewCols = previewRows.length > 0 ? Object.keys(previewRows[0]) : [];

  // Per-step validation: compute which steps have dependency errors in current order
  const pipelineErrors = validatePipelineOrder(pipeline, columns);
  const errorByStepId = Object.fromEntries(pipelineErrors.map(e => [e.stepId, e.missingColumns]));

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 bg-white border-b border-gray-200 flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate(`/staging/${uploadId}`)} className="text-gray-400 hover:text-gray-600">
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold text-gray-900">Transformation Pipeline</h1>
          <p className="text-xs text-gray-400">{upload?.filename} · {pipeline.length} steps</p>
        </div>
        <Button size="sm" variant="outline" className="border-[#006080] text-[#006080]" onClick={preview} disabled={previewing}>
          <Eye size={14} className="mr-1.5" /> {previewing ? "Previewing..." : "Preview"}
        </Button>
        <Button size="sm" className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={savePipeline} disabled={saving} data-testid="save-pipeline-btn">
          {saving ? "Saving..." : "Save Pipeline"}
        </Button>
      </div>

      {error && <div className="px-6 py-2 bg-red-50 border-b border-red-200 text-red-700 text-sm">{error}</div>}
      {dragError && (
        <div className="px-6 py-2 bg-red-50 border-b border-red-200 text-red-700 text-sm flex items-center gap-2">
          <span className="font-semibold">Cannot reorder:</span> {dragError}
        </div>
      )}

      <div className="flex-1 overflow-hidden flex">
        {/* Steps panel */}
        <div className="w-[380px] flex-shrink-0 border-r border-gray-200 flex flex-col bg-white">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Pipeline Steps</p>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {pipeline.length === 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400">No steps yet. Add your first transformation below.</p>
              </div>
            )}
            {pipeline.map((step, idx) => {
              const stepColumns = getColumnsAtStep(pipeline, columns, idx);
              const colSetBefore = new Set(getColumnsAtStep(pipeline, columns, idx));
              const newCols = getStepNewColumns(step, colSetBefore);
              const stepError = errorByStepId[step.id];

              return (
                <div
                  key={step.id}
                  draggable
                  onDragStart={e => handleDragStart(e, idx)}
                  onDragOver={e => handleDragOver(e, idx)}
                  onDrop={e => handleDrop(e, idx)}
                  onDragEnd={handleDragEnd}
                  className={`step-card transition-all ${stepError ? "border-red-300 bg-red-50/30" : expandedStep === step.id ? "step-card-active" : ""} ${dragOverIdx === idx && dragIdx !== idx ? "border-[#006080] bg-[#e6f2f5]/40 scale-[1.01]" : ""} ${dragIdx === idx ? "opacity-50" : ""}`}
                  style={{ cursor: "grab" }}
                >
                  <div
                    className="flex items-center gap-2 p-3 cursor-pointer"
                    onClick={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
                  >
                    <GripVertical size={14} className="text-gray-400 flex-shrink-0" title="Drag to reorder" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-400 font-mono">#{idx + 1}</span>
                        <span className={`text-sm font-medium ${stepError ? "text-red-600" : "text-gray-800"}`}>
                          {STEP_TYPES.find(t => t.value === step.type)?.label || step.type}
                        </span>
                        {newCols.map(c => (
                          <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-mono font-semibold">
                            +{c}
                          </span>
                        ))}
                      </div>
                      {stepError ? (
                        <p className="text-xs text-red-500 mt-0.5">
                          Missing: {stepError.map(c => `"${c}"`).join(", ")}
                        </p>
                      ) : (
                        <>
                          {step.config?.column && <p className="text-xs text-gray-400 truncate">col: {step.config.column}</p>}
                          {step.config?.output_column && !newCols.includes(step.config.output_column) && (
                            <p className="text-xs text-gray-400 truncate">→ {step.config.output_column}</p>
                          )}
                        </>
                      )}
                    </div>
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      onClick={e => { e.stopPropagation(); removeStep(step.id); }}
                    >
                      <Trash2 size={13} className="text-gray-400" />
                    </button>
                    {expandedStep === step.id ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                  </div>
                  {expandedStep === step.id && (
                    <div className="px-3 pb-3 border-t border-gray-100 pt-3">
                      {stepError && (
                        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                          Column(s) not available at this position: <strong>{stepError.join(", ")}</strong>. Move this step after the step that creates these columns.
                        </div>
                      )}
                      <StepForm
                        step={step}
                        columns={stepColumns}
                        lookupTables={lookupTables}
                        onChange={(updated) => updateStep(step.id, updated)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add step */}
          <div className="p-3 border-t border-gray-100">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">Add Step</p>
            <div className="grid grid-cols-2 gap-1">
              {STEP_TYPES.map(st => (
                <button
                  key={st.value}
                  className="text-xs px-2 py-1.5 bg-[#F8F9FA] hover:bg-[#e6f2f5] hover:text-[#006080] rounded border border-gray-100 text-gray-600 text-left transition-colors"
                  onClick={() => addStep(st.value)}
                  data-testid={`add-step-${st.value}`}
                >
                  + {st.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview panel */}
        <div className="flex-1 overflow-auto p-4">
          {previewRows.length > 0 ? (
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-3">
                Preview (first {previewRows.length} rows after transformation)
              </p>
              <div className="overflow-x-auto">
                <table className="w-full data-table text-sm">
                  <thead>
                    <tr>
                      {previewCols.map(c => <th key={c} className="px-3 py-2 text-left">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, ri) => (
                      <tr key={ri}>
                        {previewCols.map(c => (
                          <td key={c} className="px-3 py-2 max-w-[150px] truncate">
                            {row[c] === null || row[c] === undefined ? (
                              <span className="text-gray-300 italic text-xs">null</span>
                            ) : String(row[c])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Eye size={40} className="mx-auto text-gray-200 mb-3" />
                <p className="text-sm text-gray-400">Click "Preview" to see transformed data</p>
                <p className="text-xs text-gray-300 mt-1">Shows first 5 rows with all pipeline steps applied</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
